// src/lib/voix.js — L'ENTENDRE RÉPONDRE.
//
// Dimitri, 2026-09-09 : « je veux pouvoir lui dicter, et je veux l'entendre me
// répondre — quand je conduis ou que je suis en déplacement. »
//
// ══ TROIS RÈGLES, ET ELLES NE SE NÉGOCIENT PAS ═════════════════════════════
//
// 1. ⚠️ IL NE PARLE JAMAIS SANS QU'ON LE LUI AIT DEMANDÉ. Un écran
//    d'administration qui se met à parler tout seul est une catastrophe : en
//    réunion, en clientèle, dans un open space. Le choix est explicite, et il
//    est GARDÉ (localStorage) — on ne le redemande pas à chaque fois, et on ne
//    le suppose jamais.
//
// 2. ⚠️ IL SE TAIT DÈS QU'ON PART. Nouvelle question, changement d'écran,
//    onglet fermé : la voix s'arrête. Une voix qui continue après qu'on a
//    quitté est pire que pas de voix — on ne sait plus ce qu'elle lit, et on
//    ne sait plus comment l'arrêter.
//
// 3. ⚠️ IL NE MEUBLE PAS. Si le navigateur ne sait pas parler, on le DIT.
//    Pas de bouton qui ne fait rien, pas de « bientôt ».
//
// ══ CE QU'IL LIT ═══════════════════════════════════════════════════════════
//
// La prose, nettoyée de ce qui ne se prononce pas : les astérisques du gras,
// les puces, les identifiants de logement laissés tels quels (« lc1 » se lit
// mal, mais l'inventer serait pire). On ne lit PAS les vues ni les
// destinations — un dessin ne se dit pas.
//
// ══ UN SEUL ENTONNOIR (2026-09-22) ═════════════════════════════════════════
//
// ⚠️ TOUT CE QUI SORT DU HAUT-PARLEUR PASSE PAR `texteLisible`, la prose comme
// la carte. Deux normalisations parallèles divergeraient au premier correctif
// appliqué d'un seul côté — et la divergence ne se verrait pas : les deux
// continueraient de parler.
//
// Ce qui s'y normalise, et POURQUOI chacun :
//   · `→`      — « devient ». C'est le signe de l'aperçu (« 62 € → 40 € ») ;
//                une flèche se lit « flèche vers la droite », ou se tait.
//   · `€`      — « euros », et les MONTANTS en toutes lettres, centimes
//                compris (§3.3 du cadrage : jamais d'arrondi à la voix).
//   · emojis   — retirés. Les avertissements de la carte en portent trois
//                (💶 🗑️ 📤) et la synthèse les énonce ou hoquette dessus.
//   · uuid     — « un identifiant ». Lu lettre par lettre, un identifiant de
//                fil Beds24 prend une demi-minute.
//
// ⚠️ ET L'ORDRE COMPTE : on normalise AVANT de couper. Couper d'abord
// laisserait un « 85,5 » orphelin se faire dire « quatre-vingt-cinq virgule
// cinq » au tour suivant — ou pire, la moitié d'un montant.

const CLE = "fbz-voix-operateur";

function _synthese() {
  if (typeof window === "undefined") return null;
  return window.speechSynthesis || null;
}

/** Le navigateur sait-il parler ? Jamais d'exception à l'import. */
export function voixDisponible() {
  return (
    _synthese() !== null &&
    typeof window.SpeechSynthesisUtterance === "function"
  );
}

/** L'état du choix, gardé d'une session à l'autre. Défaut : MUET. */
export function voixActive() {
  try {
    return window.localStorage.getItem(CLE) === "oui";
  } catch {
    // Stockage refusé : on reste muet. Le défaut sûr est le silence.
    return false;
  }
}

export function reglerVoix(active) {
  try {
    window.localStorage.setItem(CLE, active ? "oui" : "non");
  } catch {
    /* le choix vivra le temps de la page, c'est mieux que rien */
  }
  if (!active) taire();
  return active;
}

// ── LES NOMBRES, EN TOUTES LETTRES ─────────────────────────────────────────
//
// ⚠️ POURQUOI ÉCRIRE LES MONTANTS PLUTÔT QUE DE LES LAISSER EN CHIFFRES
// (cadrage §3.3, chemin argent). La synthèse lit « 85,50 € » de trois façons
// selon le navigateur — « quatre-vingt-cinq virgule cinquante euros »,
// « quatre-vingt-cinq cinq euros », ou « quatre-vingt-cinq euros » tout court.
// La dernière est celle qui fait mal : elle PERD les centimes sans le dire.
// On écrit donc nous-mêmes, ou on ne dit pas le montant.
const _UNITES = [
  "zéro",
  "un",
  "deux",
  "trois",
  "quatre",
  "cinq",
  "six",
  "sept",
  "huit",
  "neuf",
  "dix",
  "onze",
  "douze",
  "treize",
  "quatorze",
  "quinze",
  "seize",
  "dix-sept",
  "dix-huit",
  "dix-neuf",
];
const _DIZAINES = [
  "",
  "",
  "vingt",
  "trente",
  "quarante",
  "cinquante",
  "soixante",
  "soixante",
  "quatre-vingt",
  "quatre-vingt",
];

function _souscent(n) {
  if (n < 20) return _UNITES[n];
  const d = Math.floor(n / 10);
  const u = n % 10;
  // 70 et 90 se construisent sur 60 et 80, et comptent jusqu'à dix-neuf :
  // « soixante-dix », « quatre-vingt-dix-sept ».
  const base = _DIZAINES[d];
  const reste = d === 7 || d === 9 ? u + 10 : u;
  if (reste === 0) return d === 8 ? "quatre-vingts" : base;
  // « vingt-et-un », mais « quatre-vingt-un » : le « et » saute sur 80.
  if (reste === 1 && d !== 8) return `${base}-et-un`;
  return `${base}-${_UNITES[reste]}`;
}

/**
 * Un entier en toutes lettres, de 0 à 999 999.
 * ⚠️ Rend `null` au-delà : on ne dit pas un nombre qu'on ne sait pas écrire.
 */
export function nombreEnLettres(n) {
  if (!Number.isInteger(n) || n < 0 || n > 999999) return null;
  if (n < 100) return _souscent(n);
  if (n < 1000) {
    const c = Math.floor(n / 100);
    const r = n % 100;
    const tete = c === 1 ? "cent" : `${_UNITES[c]} cent${r === 0 ? "s" : ""}`;
    return r === 0 ? tete : `${tete} ${_souscent(r)}`;
  }
  const m = Math.floor(n / 1000);
  const r = n % 1000;
  const tete = m === 1 ? "mille" : `${nombreEnLettres(m)} mille`;
  return r === 0 ? tete : `${tete} ${nombreEnLettres(r)}`;
}

/**
 * Un montant, dit comme on le dirait au téléphone.
 *
 *   « 85,50 »  → « quatre-vingt-cinq euros cinquante »
 *   « 1 234 »  → « mille deux cent trente-quatre euros »
 *   « 40 »     → « quarante euros »   (pas de « zéro centime » : ça sonne faux)
 *
 * ⚠️ FAIL-CLOSED (chemin argent). Un montant qu'on ne sait pas écrire
 * EXACTEMENT — trop grand, trois décimales, illisible — rend `null`, et
 * l'appelant dit « le montant est à l'écran ». Jamais d'arrondi : arrondir un
 * prix à la voix, c'est annoncer autre chose que ce qui va partir.
 */
export function montantDit(valeur) {
  let t = typeof valeur === "number" ? String(valeur) : String(valeur || "");
  t = t.replace(/[€\s  ]/g, "").trim();
  if (!t) return null;
  // Le séparateur de milliers disparaît ; la virgule décimale, JAMAIS.
  const m = /^(-?)(\d+)(?:[.,](\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const entier = Number(m[2]);
  const mots = nombreEnLettres(entier);
  if (mots === null) return null;
  const cents = m[3] ? Number(m[3].padEnd(2, "0")) : 0;
  const signe = m[1] === "-" ? "moins " : "";
  const euros = `${signe}${mots} euro${entier > 1 ? "s" : ""}`;
  if (!cents) return euros;
  return `${euros} ${nombreEnLettres(cents)}`;
}

const _MOIS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

/**
 * Une date, ou une paire de dates, dite en français.
 *
 *   dateDite("2026-10-12")               → « le 12 octobre »
 *   dateDite("2026-10-12", "2026-10-15") → « du 12 au 15 octobre »
 *
 * ⚠️ AUCUNE ISO NE PART À LA SYNTHÈSE. « 2026-10-12 » se lit « deux mille
 * vingt-six tiret dix tiret douze » : une suite de nombres dont personne ne
 * reconnaît une date. L'année ne se dit que si elle n'est pas l'année en cours
 * — la répéter à chaque phrase alourdit sans rien apprendre.
 */
export function dateDite(iso, iso2, aujourdhui) {
  const lire = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || "").trim());
    if (!m) return null;
    const mois = Number(m[2]);
    const jour = Number(m[3]);
    if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return null;
    return { an: Number(m[1]), mois, jour };
  };
  const a = lire(iso);
  if (!a) return null;
  const an = (
    aujourdhui instanceof Date ? aujourdhui : new Date()
  ).getFullYear();
  const suffixe = (d) => (d.an === an ? "" : ` ${d.an}`);
  // « le 1er », jamais « le 1 » : c'est le seul jour qui s'ordonne.
  const j = (d) => `${d.jour}${d.jour === 1 ? "er" : ""}`;
  const b = iso2 ? lire(iso2) : null;
  if (!b) return `le ${j(a)} ${_MOIS[a.mois - 1]}${suffixe(a)}`;
  // Même mois, même année : « du 12 au 15 octobre », le mois une seule fois.
  if (a.an === b.an && a.mois === b.mois)
    return `du ${j(a)} au ${j(b)} ${_MOIS[b.mois - 1]}${suffixe(b)}`;
  return `du ${j(a)} ${_MOIS[a.mois - 1]}${suffixe(a)} au ${j(b)} ${_MOIS[b.mois - 1]}${suffixe(b)}`;
}

// Ce qu'on retire, plutôt que de le laisser se prononcer. Les flèches (U+2192)
// tombent dans la même plage que les pictogrammes : elles sont traitées AVANT.
const _EMOJIS = /[←-⇿⌀-⏿①-⓿■-➿⬀-⯿︎️‍]|[\ud83c-\ud83e][\udc00-\udfff]/g;

// Un identifiant : ce qui mêle lettres et chiffres sur douze caractères ou
// plus, ou une uuid. ⚠️ On exige UN CHIFFRE — sinon « conciergerie » et
// « réconciliation » deviendraient « un identifiant ».
const _UUID =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const _IDENT = /(^|[\s(])([A-Za-z0-9_-]{12,})(?=$|[\s).,;:!?])/g;

// Un montant écrit en chiffres, suivi de son euro. Les séparateurs de
// milliers (espace, espace fine, espace insécable) sont admis À L'INTÉRIEUR.
const _MONTANT = /(-?\d[\d\s  ]*(?:[.,]\d{1,2})?)\s*€/g;

// La coupe et ce qu'elle annonce. Une voix qui lit trois minutes est une voix
// qu'on coupe : au-delà, la réponse se lit à l'écran, et le début suffit à
// savoir si ça vaut le coup de regarder.
const LIMITE = 700;
const SUFFIXE_COUPE = " Je m'arrête là, la suite est à l'écran.";

/**
 * Normalise, sans couper. C'est l'étape 1 de l'entonnoir.
 */
function _normaliser(brut) {
  if (typeof brut !== "string") return "";
  let t = brut
    // Le gras et l'italique de Markdown : des étoiles ne se prononcent pas.
    .replace(/\*\*?([^*]+)\*\*?/g, "$1")
    // Les puces en début de ligne deviennent des pauses, pas des « tiret ».
    .replace(/^\s*[-·•]\s*/gm, "");
  // ⚠️ LA FLÈCHE AVANT LES EMOJIS : U+2192 est dans la plage qu'on efface.
  // Inversé, « 62 € → 40 € » deviendrait « 62 euros 40 euros » — deux prix
  // collés, sans le sens qui les relie.
  t = t.replace(/\s*(→|->|⟶)\s*/g, " devient ");
  t = t.replace(_UUID, "un identifiant");
  t = t.replace(_MONTANT, (tout, nombre) => {
    const dit = montantDit(nombre);
    // Fail-SOFT dans la prose (et fail-closed dans la carte, qui, elle, refuse
    // de lire le geste) : on garde le chiffre plutôt que d'escamoter un prix.
    return dit ? ` ${dit} ` : ` ${nombre.replace(/[\s  ]/g, "")} euros `;
  });
  t = t.replace(/€/g, " euros ");
  t = t.replace(_IDENT, (tout, avant, mot) =>
    /\d/.test(mot) && /[A-Za-z]/.test(mot) ? `${avant}un identifiant` : tout,
  );
  t = t.replace(_EMOJIS, " ");
  // Les substitutions laissent des espaces avant la ponctuation (« quarante
  // euros . ») — et une synthèse en fait une PAUSE audible, au mauvais endroit.
  return t
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?…])/g, "$1")
    .trim();
}

/**
 * Coupe honnêtement : à la fin d'une phrase, sinon au dernier mot, JAMAIS au
 * milieu d'un nombre — et elle DIT qu'elle s'arrête.
 *
 * ⚠️ Exporté avec son `reste` pour que le test puisse PROUVER qu'il y avait
 * une suite (cadrage §4.1-4). Une coupe qui rend seulement le début se teste
 * par sa longueur, et une longueur ne dit pas si quelque chose a été perdu.
 *
 * ⚠️ PAS DE « je continue ? » ICI (cadrage §4.1). Une lecture par tranches
 * donnerait deux sens au mot « oui » — celui qui relance la lecture et celui
 * qui lance un geste — au moment précis où l'on construit la garde du second.
 *
 * @returns {{texte:string, reste:string}}
 */
export function coupeLisible(brut) {
  const t = _normaliser(brut);
  if (t.length <= LIMITE) return { texte: t, reste: "" };
  const plafond = LIMITE - SUFFIXE_COUPE.length;
  const tete = t.slice(0, plafond);
  // 1. La fin de phrase la plus tardive.
  const phrase = /[.!?…]\s/g;
  let coupe = -1;
  let m;
  while ((m = phrase.exec(tete)) !== null) coupe = m.index + 1;
  // 2. Le repli : la dernière espace.
  if (coupe <= 0) coupe = tete.lastIndexOf(" ");
  // 3. Jamais à l'intérieur d'un nombre — « quarante-deux mille » coupé après
  //    « quarante-deux » annoncerait un montant cinquante fois trop petit.
  while (coupe > 0 && /\d/.test(t[coupe - 1]) && /[\d.,]/.test(t[coupe] || ""))
    coupe = t.lastIndexOf(" ", coupe - 1);
  if (coupe <= 0) coupe = plafond;
  return {
    texte: `${t.slice(0, coupe).trim()}${SUFFIXE_COUPE}`,
    reste: t.slice(coupe).trim(),
  };
}

/**
 * Ce qui se prononce, à partir de ce qui s'affiche.
 * Exporté pour être testé : c'est la seule partie sans navigateur.
 */
export function texteLisible(brut) {
  return coupeLisible(brut).texte;
}

// ══ LE JETON DE GÉNÉRATION — « COUPER » NE VAUT PAS « ENTENDU » ════════════
//
// ⚠️ DÉFAUT STRUCTUREL DE LA SYNTHÈSE, ET IL EST INVERSÉ EN SILENCE
// (revue adverse B2, MAJEUR 3). `speechSynthesis.cancel()` FAIT PARTIR
// `onend` sur l'énoncé qu'il coupe — la spécification ne distingue pas « fini »
// de « interrompu ». Sans jeton, couper la parole appelle donc le rappel de
// FIN : l'empreinte « il l'a entendue » se poserait précisément sur l'énoncé
// qu'il n'a PAS entendu, et un « oui » lancerait un geste sur une phrase
// coupée à sa deuxième seconde.
//
// Et remettre le drapeau à faux dans `taire()` ne suffit pas : la remise est
// SYNCHRONE, l'événement arrive à la tâche suivante et le repose à vrai.
//
// La génération répond à la seule question qui compte : « cette fin-là est-elle
// la fin de l'énoncé COURANT ? ». `taire()` l'incrémente, chaque `dire()` aussi
// (son propre `cancel()` coupe le précédent).
let gen = 0;

/** Coupe la parole, tout de suite. Sans effet si rien ne parle. */
export function taire() {
  gen += 1;
  const s = _synthese();
  try {
    if (s) s.cancel();
  } catch {
    /* rien à couper */
  }
}

/**
 * Dit un texte.
 *
 * ⚠️ `impose` — LE DÉFAUT QU'IL CORRIGE (2026-09-09). Dimitri : « j'ai activé
 * le mode conversation, il ne parle pas. » C'était de ma faute, et la cause
 * est exactement celle-ci : `dire()` exigeait un interrupteur séparé
 * (`fbz-voix-operateur`) que j'avais RETIRÉ de l'en-tête en le remplaçant par
 * le bouton « parler ». Plus personne ne pouvait l'allumer. `dire()` rendait
 * `false`, la machine à états enchaînait sans rien dire, et rien ne le
 * signalait : un silence parfaitement plausible.
 *
 * En mode conversation, LE MODE EST LE CONSENTEMENT : on appuie sur « parler »,
 * on veut l'entendre. Exiger un second interrupteur, c'est demander deux fois
 * la même permission — et la deuxième était invisible.
 *
 * ⚠️ Hors conversation, LE SILENCE RESTE LE DÉFAUT. Un écran d'administration
 * qui parle tout seul en réunion reste une catastrophe : sans `impose`, il faut
 * toujours un choix explicite.
 *
 * ⚠️ `onFini` — APPELÉ SUR TOUS LES CHEMINS, Y COMPRIS CEUX QUI RENDENT
 * `false`. Un navigateur muet, un texte vide, une synthèse qui lève : la
 * machine à états attendrait sinon son plafond (jusqu'à quarante-cinq
 * secondes) la fin d'une phrase jamais prononcée. Le motif voyage avec :
 * 'fin' | 'erreur' | 'muet' | 'vide'.
 *
 * ⚠️ ET IL NE PART QUE SI L'ÉNONCÉ PORTE ENCORE LA GÉNÉRATION COURANTE : une
 * phrase coupée n'a pas été entendue, quoi qu'en dise `onend` (voir le jeton
 * de génération ci-dessus).
 *
 * @returns {boolean} vrai s'il parle vraiment — l'appelant peut le dire.
 */
export function dire(brut, { impose = false, onFini } = {}) {
  // ⚠️ AVANT TOUT : le `cancel()` plus bas fera partir le `onend` de l'énoncé
  // précédent. Incrémenter ici le périme, donc le fait taire.
  gen += 1;
  const moi = gen;
  const fini = (raison) => {
    if (moi !== gen) return;
    try {
      if (typeof onFini === "function") onFini(raison);
    } catch (e) {
      // Un appelant qui plante ne doit pas laisser la voix dans les limbes.
      console.error("[voix] onFini", e?.message || e);
    }
  };
  if (!impose && !voixActive()) {
    fini("muet");
    return false;
  }
  if (!voixDisponible()) {
    fini("muet");
    return false;
  }
  const texte = texteLisible(brut);
  if (!texte) {
    fini("vide");
    return false;
  }
  const s = _synthese();
  try {
    // ⚠️ On coupe AVANT de parler : deux voix superposées sont
    // incompréhensibles, et c'est ce qui arrive si une réponse arrive pendant
    // que la précédente se lit encore.
    s.cancel();
    const u = new window.SpeechSynthesisUtterance(texte);
    u.lang = "fr-FR";
    u.rate = 1.05; // à peine plus vite que le défaut : on écoute en marchant
    u.onend = () => fini("fin");
    u.onerror = () => fini("erreur");
    s.speak(u);
    return true;
  } catch (e) {
    console.error("[voix]", e?.message || e);
    fini("erreur");
    return false;
  }
}

export const RAISON_ABSENTE = "Votre navigateur ne sait pas lire à voix haute.";
